import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconCalculator, IconWarning, IconLightbulb } from '../../../icons';
import { TAX_THRESHOLDS } from '../../../types/onboardingTypes';

interface TaxData {
  taxNumber: string | null;
  vatNumber: string | null;
  isSmallBusiness?: boolean;
}

interface TaxNumberStepProps {
  initialData: TaxData;
  isCorporation: boolean;
  onSave: (data: TaxData) => Promise<void>;
  onNext: () => void;
  onBack: () => void;
  onSkip: () => void;
}

const TaxNumberStep: React.FC<TaxNumberStepProps> = ({
  initialData,
  isCorporation,
  onSave,
  onNext,
  onBack,
  onSkip,
}) => {
  const { t } = useTranslation('onboarding');
  const [formData, setFormData] = useState<TaxData>({
    taxNumber: initialData.taxNumber || '',
    vatNumber: initialData.vatNumber || '',
    isSmallBusiness: initialData.isSmallBusiness || false,
  });
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
    
    // Clear error when user types
    if (name === 'taxNumber' && error) {
      setError(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation: Tax number is mandatory if small business rule is applied
    if (formData.isSmallBusiness && (!formData.taxNumber || formData.taxNumber.trim() === '')) {
      setError(t('steps.taxNumber.requiredForSmallBusiness'));
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      await onSave({
        taxNumber: formData.taxNumber?.trim() || null,
        vatNumber: formData.vatNumber?.trim() || null,
        isSmallBusiness: formData.isSmallBusiness,
      });
      onNext();
    } catch (err) {
      // Error handling silently - user can try again
    } finally {
      setIsSaving(false);
    }
  };

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <div className="max-w-xl mx-auto">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="w-16 h-16 mx-auto bg-gradient-to-br from-indigo-500 to-[#0C1322] rounded-2xl flex items-center justify-center mb-4 shadow-lg">
          <IconCalculator size={32} className="text-primary" />
        </div>
        <h2 className="text-2xl font-bold text-primary mb-2">
          {t('steps.taxNumber.title')}
        </h2>
        <p className="text-muted">
          {t('steps.taxNumber.description')}
        </p>
        <span className="inline-block mt-2 px-3 py-1 bg-[rgb(var(--surface-inset-rgb)/0.5)] rounded-full text-muted text-sm">
          {t('common.optional')}
        </span>
      </div>

      {/* Important Notice */}
      <div className="bg-[var(--tone-warning-bg)] border border-[var(--tone-warning-border)] rounded-xl p-4 mb-6">
        <div className="flex items-start">
          <IconWarning size={20} className="text-[var(--tone-warning-fg)] mt-0.5 mr-3 flex-shrink-0" />
          <div>
            <p className="text-[var(--tone-warning-fg)] text-sm font-medium">
              {t('steps.taxNumber.advisorNotice')}
            </p>
            <p className="text-[var(--tone-warning-fg)] text-sm mt-1">
              {t('steps.taxNumber.advisorInfo')}
            </p>
          </div>
        </div>
      </div>

      {/* Kleinunternehmer Info & Checkbox */}
      {!isCorporation && (
        <div className="bg-[var(--tone-info-bg)] border border-[var(--tone-info-border)] rounded-xl p-4 mb-6">
          <div className="flex items-start mb-4">
            <IconLightbulb size={20} className="text-[var(--tone-info-fg)] mt-0.5 mr-3 flex-shrink-0" />
            <div>
              <p className="text-[var(--tone-info-fg)] text-sm font-medium">
                {t('steps.taxNumber.smallBusinessTitle')}
              </p>
              <p className="text-[var(--tone-info-fg)] text-sm mt-2">
                {t('steps.taxNumber.smallBusinessInfo', {
                  previousYear: formatCurrency(TAX_THRESHOLDS.kleinunternehmerGrenzeVorjahr),
                  currentYear: formatCurrency(TAX_THRESHOLDS.kleinunternehmerGrenzeAktuellesJahr),
                })}
              </p>
            </div>
          </div>

          <div className="flex items-center pt-2 border-t border-[var(--tone-info-border)]">
            <input
              type="checkbox"
              id="isSmallBusiness"
              name="isSmallBusiness"
              checked={formData.isSmallBusiness || false}
              onChange={handleChange}
              className="h-5 w-5 text-indigo-600 focus:ring-indigo-500 border-strong rounded bg-surface-inset"
            />
            <label htmlFor="isSmallBusiness" className="ml-3 block text-sm font-medium text-[var(--tone-info-fg)]">
              {t('steps.taxNumber.applySmallBusinessRule')}
            </label>
          </div>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Tax Number */}
        <div>
          <label htmlFor="taxNumber" className="block text-sm font-medium text-secondary mb-2">
            {t('steps.taxNumber.taxNumberLabel')}
            {formData.isSmallBusiness && <span className="text-[var(--tone-danger-fg)] ml-1">*</span>}
          </label>
          <input
            type="text"
            id="taxNumber"
            name="taxNumber"
            value={formData.taxNumber || ''}
            onChange={handleChange}
            placeholder={t('steps.taxNumber.taxNumberPlaceholder')}
            className={`w-full px-4 py-3 bg-surface-raised border rounded-xl text-primary placeholder:text-faint focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all ${
              error ? 'border-red-500' : 'border-strong'
            }`}
          />
          {error && (
            <p className="mt-1 text-[var(--tone-danger-fg)] text-sm">{error}</p>
          )}
          <p className="mt-1 text-faint text-xs">
            {t('steps.taxNumber.taxNumberHelp')}
          </p>
        </div>

        {/* VAT Number */}
        <div>
          <label htmlFor="vatNumber" className="block text-sm font-medium text-secondary mb-2">
            {t('steps.taxNumber.vatNumberLabel')}
          </label>
          <input
            type="text"
            id="vatNumber"
            name="vatNumber"
            value={formData.vatNumber || ''}
            onChange={handleChange}
            placeholder={t('steps.taxNumber.vatNumberPlaceholder')}
            className="w-full px-4 py-3 bg-surface-raised border border-strong rounded-xl text-primary placeholder:text-faint focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
          />
          <p className="mt-1 text-faint text-xs">
            {t('steps.taxNumber.vatNumberHelp')}
          </p>
        </div>

        {/* Info Box */}
        <div className="bg-[rgb(var(--surface-raised-rgb)/0.5)] rounded-xl p-4 border border-hairline">
          <p className="text-muted text-sm">
            💡 {t('steps.taxNumber.laterInfo')}
          </p>
        </div>

        {/* Buttons */}
        <div className="flex justify-between pt-4">
          <button
            type="button"
            onClick={onBack}
            className="px-6 py-3 text-muted hover:text-primary transition-colors"
          >
            {t('common.back')}
          </button>
          
          <div className="flex space-x-3">
            <button
              type="button"
              onClick={onSkip}
              disabled={formData.isSmallBusiness} // Cannot skip if small business rule is applied (tax number required)
              className={`px-6 py-3 border border-strong rounded-xl transition-colors ${
                formData.isSmallBusiness 
                  ? 'text-gray-600 border-hairline cursor-not-allowed opacity-50'
                  : 'text-muted hover:text-primary hover:border-gray-500'
              }`}
            >
              {t('common.skip')}
            </button>
            
            <button
              type="submit"
              disabled={isSaving}
              className="px-8 py-3 bg-indigo-600 text-[#0C1322] font-medium rounded-xl hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg hover:shadow-indigo-500/30"
            >
              {isSaving ? t('common.saving') : t('common.next')}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default TaxNumberStep;



